import {XIcon} from 'lucide-react';
import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	ReactNode,
	useEffect,
} from 'react';
import {
	Alert,
	AlertDescription,
} from '../../components/ui/shadcn-components/alert';
import {NotificationLevel} from '../data/general/notificationLevel.enum';
import {prepareNotification} from '../shared/utils/notification-tools';
import {translate} from '../shared/utils/localization-utils';

interface Notification {
	message: string;
	severity?: string;
	className: string;
	duration: number;
	title: string;
	IconComponent: React.ElementType;
}

interface NotificationContextProps {
	notify: (level: NotificationLevel, message: string, ...parameters: string[]) => void;
	notifyMultiple: (
		level: NotificationLevel,
		messages: {key: string; parameters: string[]}[],
		localizeParameters?: boolean
	) => void;
}

interface NotificationProviderProps {
	children: ReactNode;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export function useNotification(): NotificationContextProps {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error('useNotification must be used within a NotificationProvider');
	}
	return context;
}

const minimalDisplayDuration = 6000; // 6 sec
const maximalDisplayDuration = 6000; // 60 sec
const displayDurationPerCharacter = 80;
const multipleValuesSeparator = '\n';

export function NotificationProvider({children}: NotificationProviderProps) {
	const [notification, setNotification] = useState<Notification | null>(null);

	const prepareDisplayDuration = (message: string) => {
		return Math.min(
			Math.max(message.length * displayDurationPerCharacter, minimalDisplayDuration),
			maximalDisplayDuration
		);
	};

	const notify = useCallback(
		(level: NotificationLevel, message: string, ...parameters: string[]) => {
			const text = message;
			const duration = prepareDisplayDuration(text);
			const config = prepareNotification(level);

			setNotification({
				message: text,
				severity: config.severity,
				className: config.className,
				duration,
				title: config.title,
				IconComponent: config.IconComponent,
			});
		},
		[]
	);

	const notifyMultiple = useCallback(
		(
			level: NotificationLevel,
			messages: {key: string; parameters: string[]}[],
			localizeParameters = false
		) => {
			const finalMessage = messages.map(msg => msg.key).join(multipleValuesSeparator);
			const duration = prepareDisplayDuration(finalMessage);
			const config = prepareNotification(level);

			setNotification({
				message: finalMessage,
				severity: config.severity,
				className: config.className,
				duration,
				title: config.title,
				IconComponent: config.IconComponent,
			});
		},
		[]
	);

	const handleClose = useCallback(() => {
		setNotification(null);
	}, []);

	useEffect(() => {
		if (notification) {
			const timer = setTimeout(() => {
				handleClose();
			}, notification.duration);

			return () => clearTimeout(timer);
		}

	}, [
		notification,
		handleClose
	]);


	return (
		<NotificationContext.Provider value={{
			notify,
			notifyMultiple
		}}>
			{children}
			{notification && notification.severity != 'destroy' && (
				<div className={'fixed right-0 left-0 top-4 z-50 flex items-center justify-center mx-6'}>
					<div className={'w-full max-w-[1552px] mx-auto flex items-center justify-center'}>
						<div className={`rounded p-4 ${notification.className}`} role="alert">
							<Alert variant="default" className="flex items-start gap-3">
								<div className="flex items-center gap-3">
									<AlertDescription className="">
										{notification.message}
									</AlertDescription>
								</div>
								<button onClick={handleClose} aria-label="Close notification" className="text-gray-500 hover:text-gray-700">
									<XIcon className={`stroke-[3px] ${notification.severity == 'info' ? 'text-black' : 'text-white '}`} />
								</button>
							</Alert>
						</div>
					</div>
				</div>
			)}
		</NotificationContext.Provider>
	);
}
